"use client";

/**
 * User edits a cited value inline.
 *
 * Extracted from `compass-intake.jsx:678-702` (`.cs-adjust`, `.cs-adjlbl`,
 * `.cs-adjrow`, `.cs-adjinput` classes).
 * Renders each value as an editable row inside a bordered canvas card.
 */

import { useState } from "react";

export interface InlineAdjustValue {
  id: string;
  label: string;
  value: string;
  /** Optional citation number. */
  cite?: number;
}

export interface InlineAdjustProps {
  /** Values to adjust. */
  values: InlineAdjustValue[];
  /** Called when the user applies a new value. */
  onApply?: (id: string, value: string, note?: string) => void;
}

/**
 * Inline value adjuster: an editable form card for tweaking values during chat.
 *
 * `.cs-adjust` → border, rounded-field, bg-canvas, padded
 */
export function InlineAdjust({ values, onApply }: InlineAdjustProps) {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");

  return (
    <div
      className={[
        "border border-(--border-default)",
        "rounded-(--radius-field)",
        "bg-(--bg-canvas) p-[13px]",
      ].join(" ")}
    >
      <div className="text-[11px] tracking-[.08em] uppercase text-(--fg-muted) font-medium mb-[10px]">
        Adjust values
      </div>

      {values.map((v) => (
        <div key={v.id} className="flex items-center gap-3 mb-[8px]">
          <span className="flex-1 text-[13.5px] text-(--fg-primary) font-medium">
            {v.label}
          </span>
          <input
            type="text"
            className={[
              "w-[104px]",
              "font-[family-name:var(--font-body)] text-[14px] font-semibold text-(--fg-primary)",
              "bg-(--bg-white) border border-(--border-default)",
              "rounded-(--radius-sm)",
              "px-[10px] py-[8px]",
              "outline-none",
              "focus:border-(--fg-brand) focus:shadow-[inset_0_0_0_1px_var(--fg-brand)]",
            ].join(" ")}
            value={edits[v.id] ?? v.value}
            onChange={(e) =>
              setEdits((prev) => ({ ...prev, [v.id]: e.target.value }))
            }
          />
        </div>
      ))}

      {/* Optional note */}
      <textarea
        className={[
          "w-full mt-[8px]",
          "font-[family-name:var(--font-body)] text-[12px] leading-[1.45] text-(--fg-primary)",
          "bg-(--bg-white) border border-(--border-default)",
          "rounded-(--radius-sm)",
          "px-[10px] py-[8px]",
          "resize-y outline-none",
          "placeholder:text-(--fg-muted)",
          "focus:border-(--fg-brand) focus:shadow-[inset_0_0_0_1px_var(--fg-brand)]",
        ].join(" ")}
        rows={2}
        placeholder="Optional note explaining the change…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <div className="flex gap-[9px] mt-[11px]">
        <button
          type="button"
          className={[
            "font-[family-name:var(--font-body)] text-[13px] font-semibold",
            "bg-(--brand-navy) text-white",
            "border-0 rounded-(--radius-field)",
            "px-[16px] py-[8px]",
            "cursor-pointer",
            "hover:opacity-90",
          ].join(" ")}
          onClick={() => {
            for (const v of values) {
              const newVal = edits[v.id];
              if (newVal != null && newVal !== v.value) {
                onApply?.(v.id, newVal, note || undefined);
              }
            }
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}
